import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SupplierCard from '../components/SupplierCard';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '../components/ui/sheet';
import { useData } from '../context/DataContext';
import { api } from '../lib/api';
import { ChevronRight, SlidersHorizontal } from 'lucide-react';

const CategoryPage = () => {
  const { slug } = useParams();
  const { tree, suppliers, loading } = useData();
  const [products, setProducts] = useState([]);
  const [sortBy, setSortBy] = useState('relevance');
  const [sellerTypes, setSellerTypes] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // find category in tree by slug or id
  let cat = null;
  let main = null;
  for (const m of tree) {
    const found = (m.categories || []).find((c) => c.slug === slug || c.id === slug);
    if (found) { cat = found; main = m; break; }
  }

  useEffect(() => {
    if (!cat) return;
    api.getProducts({ category_id: cat.id, sort: sortBy, limit: 60 })
      .then((d) => setProducts(d.items || [])).catch(() => {});
  }, [cat, sortBy]);

  useEffect(() => {
    // Admin-managed Seller Type catalogue drives this filter. We use the
    // public endpoint (which already excludes verified_only=true entries —
    // the verified badges still show on individual cards but are not
    // filterable as a self-applied trait).
    api.getSellerTypes({ includeVerifiedOnly: true })
      .then(setSellerTypes)
      .catch(() => setSellerTypes([]));
  }, []);

  if (loading) {
    return <div><Header /><div className="max-w-7xl mx-auto px-4 py-20 text-center text-gray-500">Loading...</div><Footer /></div>;
  }
  if (!cat) {
    return (
      <div>
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold">Category not found</h1>
          <Link to="/" className="text-[#2c54b2] hover:underline mt-4 inline-block">Go back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const filterContent = (
    <>
      <div>
        <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Sub-categories</h4>
        <div className="space-y-1">
          {(cat.sub_categories || []).map((sc) => (
            <Link key={sc.id} to={`/subcategory/${sc.slug}`}
              onClick={() => setFiltersOpen(false)}
              className="block text-sm text-gray-700 hover:text-[#ee5e23] py-1 transition-colors">
              {sc.name}
            </Link>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Seller Type</h4>
        {sellerTypes.length === 0 ? (
          <p className="text-xs text-gray-400">No seller types yet.</p>
        ) : sellerTypes.map((st) => (
          <label key={st.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-[#2c54b2] py-1" data-testid={`seller-type-filter-${st.slug}`}>
            <Checkbox /> {st.name}
          </label>
        ))}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-1 text-xs text-gray-600 flex-wrap">
          <Link to="/" className="hover:text-[#2c54b2]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          {main && (<><Link to={`/main/${main.slug}`} className="hover:text-[#2c54b2]">{main.name}</Link><ChevronRight className="w-3 h-3" /></>)}
          <span className="text-gray-900 font-medium">{cat.name}</span>
        </div>
      </div>

      <section className="bg-gradient-to-r from-[#2c54b2] to-[#1e3a8a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-5 sm:py-8 flex items-center gap-4 sm:gap-6">
          <img src={cat.image} alt={cat.name} className="w-14 h-14 sm:w-24 sm:h-24 rounded-lg object-cover border-2 sm:border-4 border-white/20 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold leading-tight">{cat.name}</h1>
            <p className="text-blue-100 mt-1 text-xs sm:text-base">{cat.products_count} • Verified Vendors across Telangana</p>
            <div className="hidden sm:flex flex-wrap gap-2 mt-3">
              {(cat.sub_categories || []).slice(0, 6).map((sc) => (
                <Link key={sc.id} to={`/subcategory/${sc.slug}`} className="px-3 py-1 bg-white/15 hover:bg-white/25 border border-white/20 rounded-full text-xs transition-colors">{sc.name}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="hidden lg:block bg-white border border-gray-200 rounded-md p-4 h-fit">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-200 mb-3">
            <SlidersHorizontal className="w-4 h-4" />
            <h3 className="font-semibold">Filters</h3>
          </div>
          {filterContent}
        </aside>

        <div>
          <div className="lg:hidden mb-4">
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-filters-btn" className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-[#ee5e23] transition-colors">
                  <SlidersHorizontal className="w-4 h-4" /> Filters &amp; Sub-categories
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] overflow-y-auto" data-testid="mobile-filters-drawer">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                  <SheetDescription className="sr-only">Filter products by sub-category and seller type</SheetDescription>
                </SheetHeader>
                <div className="mt-4">{filterContent}</div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="bg-white border border-gray-200 rounded-md px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700">{products.length} products found</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Sort by:</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-[#ee5e23]">
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
          {products.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-md py-16 text-center">
              <p className="text-gray-600">No products yet in this category.</p>
              <Link to="/" className="text-[#2c54b2] hover:underline mt-2 inline-block">Browse other categories</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}

          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Top Vendors</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.slice(0, 6).map((s) => <SupplierCard key={s.id} supplier={s} />)}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CategoryPage;
